import { message } from 'antd';
import ct from 'countries-and-timezones';
import * as htmlToImage from 'html-to-image';
import {
  camelCase,
  filter,
  includes,
  isArray,
  isNumber,
  lowerCase,
  startCase,
} from 'lodash';
import { evaluate } from 'mathjs';
import moment from 'moment-timezone';
import Resizer from 'react-image-file-resizer';
import watermark from 'watermarkjs';
import countryCodeData from '../assets/countryCode.json';
import {
  DATE_ARITHMETIC_FUNCTIONS,
  DATE_FUNCTION_TYPES,
  DATE_FUNCTIONS,
  DEFAULTDATEFORMAT,
  DEFAULTDATETIMEFORMATFORREPORTS,
  DIRECTIONS,
  ELEMENT_IDS,
  FIELD_TYPES,
  INVALID_FORMULA_MESSAGE,
  PROPERTY_TYPES,
  REGEX,
  TIME_12_HOUR_FORMAT,
  TIME_24_HOUR_FORMAT,
  TIME_FUNCTION_TYPES,
  TIME_FUNCTIONS,
} from './constants';

// Portal related methods
export const injectUsingPortal = (portalId) => {
  // eslint-disable-next-line no-undef
  return document.getElementById(portalId);
};

export const isPortalIdExists = (portalId) => {
  return !!injectUsingPortal(portalId);
};

// Check for document Id's exists
export const getElementFromDocumentId = (portalId) => {
  // eslint-disable-next-line no-undef
  return document.getElementById(portalId);
};

export const isDocumentIdExist = (portalId) => {
  return !!getElementFromDocumentId(portalId);
};
// Check for document Id's exists end

export const formatDate = (
  datetime,
  format = `${DEFAULTDATEFORMAT} hh:mm A`,
) => {
  if (datetime && moment && format) {
    return moment(datetime).format(format);
  }

  return datetime;
};

export const formValidatorRules = {
  required: {
    required: true,
    message: 'Required',
  },
  email: {
    type: 'email',
    message: 'Please enter a valid email',
  },
  characterWithoutWhiteSpace: {
    pattern: REGEX.CHARACTER_WITHOUT_WHITESPACE_IN_STARTING_AND_ENDING,
    message: 'Enter proper value!',
  },
  number: () => ({
    validator(rule, value) {
      if (!value) {
        return Promise.resolve();
      }
      if (!Number(value) || !REGEX.NUMBER.test(Number(value))) {
        // eslint-disable-next-line prefer-promise-reject-errors
        return Promise.reject('Should be a valid Number');
      }
      return Promise.resolve();
    },
  }),
  numberWithDecimal: () => ({
    validator(rule, value) {
      if (!value) {
        return Promise.resolve();
      }
      if (!REGEX.POSITIVE_DECIMAL_NUMBER.test(value)) {
        // eslint-disable-next-line prefer-promise-reject-errors
        return Promise.reject('Should be a valid Number');
      }
      return Promise.resolve();
    },
  }),
  password: () => ({
    validator(rule, value) {
      if (!value) {
        return Promise.resolve();
      }
      if (!REGEX.PASSWORD.test(value)) {
        // eslint-disable-next-line prefer-promise-reject-errors
        return Promise.reject();
      }
      return Promise.resolve();
    },
  }),
};

export const combineDateTimeAndGetISOString = (date, time) => {
  const timeObj = new Date(time);
  const dateObj = new Date(date);

  let formattedDateTime = dateObj.setUTCHours(timeObj.getUTCHours());
  formattedDateTime = new Date(formattedDateTime).setUTCMinutes(
    timeObj.getUTCMinutes(),
  );
  formattedDateTime = new Date(formattedDateTime).toISOString();

  return formattedDateTime;
};

export const formatPhoneNumber = (str) => {
  // Filter only numbers from the input
  const cleaned = `${str}`.replace(/\D/g, '');

  // Check if the input is of correct length
  const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);

  if (match) {
    return `(${match[1]}) ${match[2]}-${match[3]}`;
  }

  return null;
};

export const formatPhoneNumberWithoutMask = (str) => {
  // Filter only numbers from the input
  const cleaned = `${str}`.replace(/\D/g, '');
  if (cleaned) return cleaned;
  return null;
};

export const formatPrice = (price) => {
  const formatedPrice = price || 0;

  return Number(formatedPrice).toLocaleString('en', {
    style: 'currency',
    currency: 'USD',
  });
};

export const formItemProps = { normalize: (value) => value.trim() };

export async function fileUpload(signedUrl, image, onUploadProgress) {
  return new Promise((resolve, reject) => {
    try {
      // eslint-disable-next-line no-undef
      const xhr = new XMLHttpRequest();
      xhr.open('PUT', signedUrl);
      xhr.setRequestHeader('Content-Type', image.type);
      xhr.addEventListener('error', (error) => {
        if (error) {
          reject(error);
        }
      });
      xhr.addEventListener('load', function () {
        if (this.readyState === 4) {
          resolve(xhr.response);
        }
      });
      if (onUploadProgress) {
        xhr.upload.onprogress = (e) => {
          let percentComplete = 0;
          percentComplete = Math.ceil((e.loaded / e.total) * 100);
          onUploadProgress(percentComplete);
        };
      }
      xhr.send(image);
    } catch (error) {
      message.error(error.message);
    }
  });
}

export const titleCase = (str = '') => startCase(camelCase(str));
export const toCamelCase = (str) => {
  return str
    ?.toLowerCase()
    ?.replace(/[^a-zA-Z0-9]+(.)/g, (match, chr) => chr?.toUpperCase());
};

// Sanitize field names by removing all non-alphanumeric characters and converting to camelCase
export const sanitizeFieldName = (str) => {
  return str
    ?.replace(/[^a-zA-Z0-9]/g, ' ') // Replace all non-alphanumeric chars with space
    ?.trim() // Remove leading/trailing spaces
    ?.replace(/\s+(.)/g, (match, chr) => chr?.toUpperCase()) // Capitalize after spaces
    ?.replace(/\s/g, '') // Remove all spaces
    ?.replace(/^(.)/, (match) => match?.toLowerCase()); // Ensure first char is lowercase
};

export function getBase64(file) {
  return new Promise((resolve, reject) => {
    // eslint-disable-next-line no-undef
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });
}

export function timeTaken(startDate, EndDate) {
  // eslint-disable-next-line no-nested-ternary
  return moment(EndDate).diff(startDate, 'minutes', true) >= 60
    ? moment(EndDate).diff(startDate, 'minutes', true) % 60 === 0
      ? `${Math.abs(moment(EndDate).diff(startDate, 'hours', true)).toFixed(
          2,
        )} h`
      : `${
          Math.abs(moment(EndDate).diff(startDate, 'hours', true))
            .toFixed(2)
            .split('.')[0]
        } h ${
          Math.abs(moment(EndDate).diff(startDate, 'hours', true))
            .toFixed(2)
            .split('.')[1]
        }mins`
    : `${Math.abs(moment(EndDate).diff(startDate, 'minutes', true)).toFixed(
        0,
      )} mins`;
}

export const checkPasswordCharacters = (value) => {
  const data = [];
  if (value?.length >= 8 && value?.length <= 16) {
    data.push('8_letter');
  }
  if (/[A-Z]/.test(value)) {
    data.push('caps');
  }
  if (/[0-9]/.test(value)) {
    data.push('number');
  }
  if (/[a-z]/.test(value)) {
    data.push('small');
  }
  if (/[!@#$%^&*(),.?":{}|<>]/.test(value)) {
    data.push('special');
  }
  return data;
};

export const removeHistoryStateData = (
  navigate,
  location,
  field,
  subField = '',
) => {
  const data = { ...location?.state };
  if (subField) {
    delete data[field]?.[subField];
  } else {
    delete data[field];
  }
  navigate(location?.pathname, data);
};

export const compressImage = ({
  file,
  maxwidth = 1000,
  maxHeight = 1000,
  fileType,
  quality = 50,
  callBack,
  outputType = 'file',
}) => {
  // eslint-disable-next-line no-new
  return new Promise((resolve) => {
    Resizer.imageFileResizer(
      file,
      maxwidth,
      maxHeight,
      fileType,
      quality,
      0,
      (uri) => {
        resolve(callBack(uri));
      },
      outputType,
    );
  });
};

export function dataURLtoFile(dataurl, filename) {
  const arr = dataurl?.split(',');
  const mime = arr?.[0]?.match(/:(.*?);/)?.[1];
  const bstr = atob(arr?.[arr?.length - 1]);
  let n = bstr?.length;
  const u8arr = new Uint8Array(n);
  // eslint-disable-next-line no-plusplus
  while (n--) {
    u8arr[n] = bstr?.charCodeAt(n);
  }
  // eslint-disable-next-line no-undef
  return new File([u8arr], filename, { type: mime });
}

export const addWatermark = async (file) => {
  const timeStamp = await htmlToImage
    // eslint-disable-next-line no-undef
    .toPng(document.querySelector(`#${ELEMENT_IDS?.TIME_STAMP}`), {
      fontEmbedCSS: '',
    })
    .then((dataUrl) => {
      return dataUrl;
    })
    .catch((error) => {
      // eslint-disable-next-line no-console
      console.error('oops, something went wrong!', error);
    });
  const imageWithTimeStamp = await watermark([file, timeStamp])
    .image(watermark.image.lowerRight())
    .then((img) => {
      return img?.src;
    });
  return imageWithTimeStamp;
};

export function copyToClipboard(text, messageText) {
  // eslint-disable-next-line no-undef
  navigator.clipboard.writeText(text).then(() => {
    message.destroy();
    message.success(messageText);
  });
}

// export const downloadImageByHtmlNode = async (
//   selector,
//   nameOfImage,
//   chartRef
// ) => {
//   // eslint-disable-next-line no-undef
//   const rr = document.querySelector(selector);
//   const selectedElement = rr.cloneNode(true);
//   selectedElement.style.backgroundColor = 'white';
//   // eslint-disable-next-line no-undef
//   const iDiv = document.createElement('div');
//   iDiv.style.height = `${chartRef?.current?.scrollHeight}px`;
//   iDiv.style.width = `${chartRef?.current?.scrollWidth}px`;

//   // eslint-disable-next-line no-undef
//   document.body.appendChild(iDiv);

//   iDiv.appendChild(selectedElement);

//   // eslint-disable-next-line no-console
//   console.log(
//     'selectedElement',
//     chartRef?.current?.scrollHeight,
//     chartRef?.current?.scrollWidth,
//     iDiv
//   );
//   // eslint-disable-next-line no-undef
//   const timeStamp = await htmlToImage.toJpeg(iDiv).then((dataUrl) => {
//     return dataUrl;
//   });
//   // eslint-disable-next-line no-undef
//   document.body.removeChild(iDiv);
//   // eslint-disable-next-line no-undef
//   const a = document.createElement('a');
//   a.href = timeStamp;
//   a.download = `${nameOfImage}.jpeg`;
//   // eslint-disable-next-line no-undef
//   document.body.appendChild(a);
//   a.click();
//   // eslint-disable-next-line no-undef
//   document.body.removeChild(a);
// };

export const downloadImageByHtmlNode = async (
  selector,
  nameOfImage,
  chartRef,
) => {
  // eslint-disable-next-line no-undef
  const containerDiv = document.createElement('div');
  try {
    // eslint-disable-next-line no-undef
    const selectedElement = document.querySelector(selector)?.cloneNode(true);
    selectedElement.style.backgroundColor = 'white';

    containerDiv.appendChild(selectedElement);

    if (chartRef && chartRef.current) {
      containerDiv.style.height = `${chartRef.current.scrollHeight}px`;
      containerDiv.style.minWidth = `${chartRef.current.scrollWidth}px`;
    }

    // eslint-disable-next-line no-undef
    document.body.appendChild(containerDiv);

    const timeStamp = await htmlToImage.toJpeg(containerDiv).then((dataUrl) => {
      return dataUrl;
    });

    // eslint-disable-next-line no-undef
    const link = document.createElement('a');
    link.href = timeStamp;
    link.download = `${nameOfImage}.jpeg`;

    link.click();
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error:', error);
  } finally {
    // eslint-disable-next-line no-undef
    document.body.removeChild(containerDiv);
  }
};

export function getPhoneCodeFromTimezone(timezone) {
  const tz = ct?.getTimezone(timezone);
  const phoneCode = filter(
    countryCodeData,
    (data) =>
      data?.countryCode?.toLocaleLowerCase() ===
      tz?.countries[0]?.toLocaleLowerCase(),
  )?.[0]?.dialCode;
  return phoneCode;
}

export function getUniqueImages(files) {
  const uniqueSet = new Set();
  return files.filter((file) => {
    const baseName = file.replace(/_\d+\.\w+$/, (match) =>
      match.slice(match.lastIndexOf('.')),
    );
    if (!uniqueSet.has(baseName)) {
      uniqueSet.add(baseName);
      return true;
    }
    return false;
  });
}

export const getCountryCodeFromLocaleTimeZone = () => {
  const timezoneSE = moment.tz.guess();
  const countryCode = moment.tz.zone(timezoneSE).countries();
  if (isArray(countryCode)) {
    return lowerCase(countryCode[0]);
  }
  return 'in';
};

export const formatNumericInput = (value) => {
  if (!value) return '';
  const numericValue = value?.replace(/[^\d.-]/g, '');
  const parts = numericValue?.split('.');
  if (parts?.length > 2) {
    return `${parts[0]}.${parts[1]}`;
  }
  if (parts?.length === 2) {
    const integerPart = parts[0];
    const decimalPart = parts[1]?.slice(0, 3);
    if (integerPart?.startsWith('-')) {
      return `${integerPart}.${decimalPart}`;
    }
    return `${integerPart}.${decimalPart}`;
  }
  return numericValue;
};

export const decimalDegreesToDegreesMinutesSeconds = (coordinate, isLat) => {
  const absolute = Math.abs(coordinate);
  const degrees = Math.floor(absolute);
  const minutesNotTruncated = (absolute - degrees) * 60;
  const minutes = String(Math.floor(minutesNotTruncated))?.padStart(2, '0');
  const seconds = String(
    ((minutesNotTruncated - minutes) * 60).toFixed(1),
  )?.padStart(4, '0');

  let direction;
  if (isLat) {
    direction = coordinate >= 0 ? DIRECTIONS.NORTH : DIRECTIONS.SOUTH;
  } else {
    direction = coordinate >= 0 ? DIRECTIONS.EAST : DIRECTIONS.WEST;
  }

  return `${degrees}°${minutes}'${seconds}"${direction}`;
};

const getFieldValueByUUID = (uuid, rowIndex, values, tableHeaders) => {
  const field = filter(tableHeaders, (record) => uuid === record?.id)?.[0];
  if (!field) return '';

  const fieldValue = values?.[`${rowIndex}_${field?.index}`];

  if (field?.type === FIELD_TYPES.DATE && fieldValue) {
    if (moment?.isMoment(fieldValue)) {
      return fieldValue?.format(DEFAULTDATEFORMAT);
    }
    if (moment(fieldValue, DEFAULTDATEFORMAT)?.isValid()) {
      return moment(fieldValue, DEFAULTDATEFORMAT)?.format(DEFAULTDATEFORMAT);
    }
  }
  if (field?.type === FIELD_TYPES.TIME && fieldValue) {
    if (moment?.isMoment(fieldValue)) {
      return fieldValue?.format(TIME_24_HOUR_FORMAT);
    }
    if (moment?.utc(fieldValue)?.isValid()) {
      return moment(new Date(fieldValue))?.format(TIME_24_HOUR_FORMAT);
    }
  }
  return fieldValue || '';
};

export const calculateFieldValue = (fieldName, form, tableHeaders) => {
  const [rowIndex, name] = fieldName?.split('_');
  const values = form?.getFieldsValue();
  const formulas = tableHeaders
    ?.filter((item) => item?.index === name)
    ?.flatMap((item) => item?.options || [])
    ?.map((str) => str?.replace(/"/g, ''))?.[0];
  if (!formulas) return '';
  try {
    let processedFormula = formulas;
    const containsDateFunctions = DATE_FUNCTIONS?.some((func) =>
      processedFormula?.includes(`${func}`),
    );
    const containsTimeFunctions = TIME_FUNCTIONS?.some((func) =>
      processedFormula?.includes(`${func}`),
    );
    const parseParams = (params) => {
      return params
        ?.split(',')
        ?.map((param) => param?.trim()?.replace(/"/g, ''));
    };

    const uuidMatches = formulas?.match(REGEX.UUID) || [];
    if (uuidMatches?.length > 0) {
      const hasAnyFieldValue = uuidMatches?.some((uuid) => {
        const fieldValue = getFieldValueByUUID(
          uuid,
          rowIndex,
          values,
          tableHeaders,
        );
        return fieldValue && fieldValue !== '' && fieldValue !== '0';
      });

      if (!hasAnyFieldValue) {
        return '-';
      }
    }

    if (containsDateFunctions) {
      DATE_FUNCTIONS.forEach((funcName) => {
        const funcRegex = new RegExp(`${funcName}\\((.*?)\\)`, 'g');
        processedFormula = processedFormula?.replace(
          funcRegex,
          (match, params) => {
            const paramList = parseParams(params);
            if (funcName === DATE_FUNCTION_TYPES.DAYS_BETWEEN) {
              if (paramList?.length !== 2) return '-';
              const date1Value = getFieldValueByUUID(
                paramList[0],
                rowIndex,
                values,
                tableHeaders,
              );
              const date2Value = getFieldValueByUUID(
                paramList[1],
                rowIndex,
                values,
                tableHeaders,
              );
              if (!date1Value || !date2Value) return '-';
              const moment1 = moment(date1Value, DEFAULTDATEFORMAT);
              const moment2 = moment(date2Value, DEFAULTDATEFORMAT);
              if (!moment1?.isValid() || !moment2?.isValid()) return '-';
              return moment1?.diff(moment2, 'days')?.toString();
            }
            if (
              includes(
                [
                  DATE_FUNCTION_TYPES.ADD_DAYS,
                  DATE_FUNCTION_TYPES.SUBTRACT_DAYS,
                ],
                funcName,
              )
            ) {
              if (paramList?.length !== 2) return `'${DEFAULTDATEFORMAT}'`;
              const dateValue = getFieldValueByUUID(
                paramList[0],
                rowIndex,
                values,
                tableHeaders,
              );
              const daysParam = paramList[1];

              if (!dateValue) return `"${DEFAULTDATEFORMAT}"`;

              let daysToAdd = 0;
              if (REGEX.UUID?.test(daysParam)) {
                daysToAdd =
                  getFieldValueByUUID(
                    daysParam,
                    rowIndex,
                    values,
                    tableHeaders,
                  ) || 0;
              } else {
                daysToAdd = daysParam || 0;
              }
              if (funcName === DATE_FUNCTION_TYPES.SUBTRACT_DAYS) {
                daysToAdd = -daysToAdd;
              }
              const baseMoment = moment(dateValue, DEFAULTDATEFORMAT);
              if (!baseMoment?.isValid()) return `"${DEFAULTDATEFORMAT}"`;
              const resultDate = baseMoment?.add(daysToAdd, 'days');
              return `"${resultDate?.format(DEFAULTDATEFORMAT)}"`;
            }
          },
        );
      });
    }
    if (containsTimeFunctions) {
      TIME_FUNCTIONS?.forEach((funcName) => {
        const funcRegex = new RegExp(`${funcName}\\((.*?)\\)`, 'g');
        processedFormula = processedFormula?.replace(
          funcRegex,
          (match, params) => {
            const paramList = parseParams(params);
            if (funcName === TIME_FUNCTION_TYPES.TIME_SUBTRACTION) {
              if (paramList?.length !== 2) return '-';
              const time1Value = getFieldValueByUUID(
                paramList[0],
                rowIndex,
                values,
                tableHeaders,
              );
              const time2Value = getFieldValueByUUID(
                paramList[1],
                rowIndex,
                values,
                tableHeaders,
              );
              if (!time1Value || !time2Value) return '-';
              const time1 = moment(time1Value, TIME_24_HOUR_FORMAT);
              const time2 = moment(time2Value, TIME_24_HOUR_FORMAT);
              if (!time1?.isValid() || !time2?.isValid()) return '-';
              const diffHours = time1
                ?.diff(time2, 'hours', true)
                ?.toFixed(2)
                ?.toString();
              return `${diffHours}`;
            }
            return '-';
          },
        );
      });
      return processedFormula || '-';
    }
    const modifiedExpValue = processedFormula?.replace(REGEX.UUID, (match) => {
      const fieldValue = getFieldValueByUUID(
        match,
        rowIndex,
        values,
        tableHeaders,
      );
      // Treat '-' (no data indicator) as '0' for calculations
      return fieldValue && fieldValue !== '-' ? fieldValue : '0';
    });
    if (
      DATE_ARITHMETIC_FUNCTIONS?.some((func) => formulas?.includes(`${func}`))
    ) {
      const dateMatch = modifiedExpValue?.match(/^"(.+)"$/);
      if (dateMatch) {
        return dateMatch[1];
      }
    }
    const result = evaluate(modifiedExpValue);
    if (isNumber(result)) {
      if (containsDateFunctions) {
        return Math.round(Number(result))?.toString();
      }
      return Number(result)?.toFixed(2)?.toString();
    }
    return '';
  } catch (error) {
    return '';
  }
};

export const extractMathFunctionParams = (formula, funcName) => {
  const funcIndex = formula?.indexOf(`${funcName}(`);
  if (funcIndex === -1) return null;

  const startIndex = funcIndex + funcName?.length + 1;
  let depth = 1;
  let inQuotes = false;
  let endIndex = startIndex;

  while (endIndex < formula?.length && depth > 0) {
    const char = formula?.[endIndex];

    if (char === '"' && formula[endIndex - 1] !== '\\') {
      inQuotes = !inQuotes;
    } else if (!inQuotes) {
      if (char === '(') {
        depth += 1;
      } else if (char === ')') {
        depth -= 1;
      }
    }
    if (depth > 0) {
      endIndex += 1;
    }
  }

  if (depth === 0) {
    return formula?.substring(startIndex, endIndex);
  }

  return null;
};

export const regexPatterns = {
  functionWithParams: (func) => new RegExp(`${func}\\([^)]+\\)`, 'g'),
  functionWithParamsCapture: (func) => new RegExp(`${func}\\(([^)]+)\\)`, 'g'),
  functionIncomplete: (funcs) => new RegExp(`\\b(${funcs.join('|')})\\([^)]*$`),
  addDaysValidation: () => /addDays\([^,]+,\s*(\d+)\s*\)/g,
  subtractDaysValidation: () => /subtractDays\([^,]+,\s*(\d+)\s*\)/g,
};

/**
 * Validates function parameters based on the function type
 * @param {string} value - The formula value containing the function
 * @param {string} functionType - The function type to validate (e.g., DAYS_BETWEEN, ADD_DAYS)
 * @param {Function} validateParams - Custom validation function for parameters
 * @returns {boolean|Promise} - Returns true if valid, or Promise rejection if invalid
 */
export const validateFunctionParams = (value, functionType, validateParams) => {
  if (!value?.includes(functionType)) {
    return true;
  }

  const functionRegex = regexPatterns?.functionWithParamsCapture(functionType);
  const matches = [...value.matchAll(functionRegex)];

  if (matches?.length === 0) {
    return true;
  }

  const params = matches?.[0]?.[1]?.split(',')?.map((p) => p?.trim());

  if (params?.length !== 2) {
    return Promise?.reject(new Error(INVALID_FORMULA_MESSAGE));
  }

  // Execute custom validation logic
  const isValid = validateParams(params);

  if (!isValid) {
    return Promise?.reject(new Error(INVALID_FORMULA_MESSAGE));
  }

  return true;
};

export const getFilteredFields = (types, calcType, fields, selectedField) => {
  return fields?.filter((field) => {
    const isMatch =
      types?.includes(field?.type) ||
      (field?.type === FIELD_TYPES.CALCULATION &&
        field?.calculationResultType === calcType);

    return selectedField?.id
      ? isMatch && field?.id !== selectedField?.id
      : isMatch;
  });
};

export const transformAggregatesForExportURL = (aggregates) => {
  return Object?.entries(aggregates)?.reduce((acc, [id, metrics]) => {
    metrics?.forEach((metric) => {
      acc[metric] = [...(acc?.[metric] || []), id];
    });
    return acc;
  }, {});
};

export const formatDisplayText = (fieldType, text) => {
  if (fieldType === PROPERTY_TYPES?.TIME && text) {
    const parsed = moment(text, TIME_24_HOUR_FORMAT, true);
    if (parsed?.isValid()) {
      return parsed?.format(TIME_12_HOUR_FORMAT);
    }
  }
  if (fieldType === PROPERTY_TYPES?.DATE && text) {
    const parsed = moment(text, DEFAULTDATETIMEFORMATFORREPORTS, true);
    if (parsed?.isValid()) {
      return parsed?.format(DEFAULTDATEFORMAT);
    }
  }
  return text;
};
